import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SelectableCustomer, matchesFilters } from 'Integrations/index'
import { useConnectedIntegrationCustomers } from 'Customers/utils/useConnectedIntegrationCustomers'
import {
  selectedCustomerIdsAtom,
  selectedIntegrationAtom,
  selectedIntegrationNameAtom
} from '../atoms'
import { getCustomerInvalidReason } from '../utils/getCustomerInvalidReason'

const selectableIdsOf = (customers: SelectableCustomer[]) =>
  customers
    .filter(customer => !customer.isDisabled)
    .map(customer => customer.id)

export const useCustomerSelection = () => {
  const [search, setSearch] = useState('')
  const [countryFilter, setCountryFilter] = useState<string[]>([])
  const [selectedCustomerIds, setSelectedCustomerIds] = useAtom(
    selectedCustomerIdsAtom
  )
  const selectedIntegration = useAtomValue(selectedIntegrationAtom)
  const selectedIntegrationName = useAtomValue(selectedIntegrationNameAtom)

  const { isLoading, isError, integrations } =
    useConnectedIntegrationCustomers()

  const selectedIntegrationCustomers = useMemo(
    () =>
      integrations?.find(
        integration => integration.service === selectedIntegration
      )?.customers ?? [],
    [integrations, selectedIntegration]
  )

  const customers: SelectableCustomer[] = useMemo(
    () =>
      selectedIntegrationCustomers
        .filter(customer => !customer.isLinked)
        .map(customer => {
          const invalidReason = getCustomerInvalidReason(customer)

          return {
            id: customer.id,
            name: customer.companyName?.trim() || 'Unnamed company',
            country: customer.country,
            isDisabled: Boolean(invalidReason),
            invalidReason,
            validationErrors: customer.validationErrors,
            externalUrl: customer.externalUrl,
            contacts: customer.contacts
          }
        }),
    [selectedIntegrationCustomers]
  )

  const countryOptions = useMemo(() => {
    const countries = Array.from(
      new Set(
        customers.flatMap(customer =>
          customer.country ? [customer.country] : []
        )
      )
    ).sort()
    return countries.map(country => ({ label: country, value: country }))
  }, [customers])

  const hasActiveFilters = Boolean(search) || countryFilter.length > 0

  const filteredCustomers = useMemo(
    () =>
      customers.filter(customer =>
        matchesFilters(customer, { search, countries: countryFilter })
      ),
    [customers, search, countryFilter]
  )

  // Select-all and the import act on the current view, so a filtered-out row is
  // never selectable and never imported until its filter is cleared.
  const selectableIdsInView = useMemo(
    () => selectableIdsOf(filteredCustomers),
    [filteredCustomers]
  )
  const selectedIdsInView = useMemo(
    () =>
      selectedCustomerIds.filter(id =>
        filteredCustomers.some(customer => customer.id === id)
      ),
    [selectedCustomerIds, filteredCustomers]
  )
  const allInViewSelected =
    selectableIdsInView.length > 0 &&
    selectableIdsInView.every(id => selectedCustomerIds.includes(id))

  const onToggleCustomer = (id: string) =>
    setSelectedCustomerIds(
      selectedCustomerIds.includes(id)
        ? selectedCustomerIds.filter(customerId => customerId !== id)
        : [...selectedCustomerIds, id]
    )

  const onToggleSelectAllInView = () =>
    setSelectedCustomerIds(
      allInViewSelected
        ? selectedCustomerIds.filter(id => !selectableIdsInView.includes(id))
        : [...new Set([...selectedCustomerIds, ...selectableIdsInView])]
    )

  const onCountryFilterChange = (countries: string[]) =>
    setCountryFilter(countries)

  const onClearFilters = () => {
    setSearch('')
    setCountryFilter([])
  }

  // Pre-select every importable customer once the list first loads; the ref
  // resets on remount, so returning to this step starts all-selected again.
  const hasInitialisedSelection = useRef(false)
  useEffect(() => {
    if (hasInitialisedSelection.current || isLoading) {
      return
    }
    hasInitialisedSelection.current = true
    setSelectedCustomerIds(selectableIdsOf(customers))
  }, [isLoading, customers, setSelectedCustomerIds])

  const submitDisabled = !selectedIntegration || selectedIdsInView.length === 0
  const submitDisabledReason =
    selectedIdsInView.length === 0
      ? 'Please select at least one customer'
      : undefined

  return {
    isLoading,
    isError,
    selectedIntegration,
    filteredCustomers,
    countryOptions,
    countryFilter,
    onCountryFilterChange,
    hasActiveFilters,
    onClearFilters,
    unfilteredCustomerCount: selectedIntegrationCustomers.length,
    allCustomersLinked:
      selectedIntegrationCustomers.length > 0 && customers.length === 0,
    selectableIdsInView,
    allInViewSelected,
    onToggleSelectAllInView,
    onToggleCustomer,
    selectedCustomerIds,
    selectedIdsInView,
    integrationName: selectedIntegrationName,
    search,
    setSearch,
    setSelectedCustomerIds,
    submitDisabled,
    submitDisabledReason
  }
}
